// Form Types - FEMA ICS Form Designer

import type { BadgeVariant } from '@/components/common/Badge';

// ============================================================================
// Field Schema Types
// ============================================================================

export interface FormSection {
  id: string;
  name: string;
  label: string;
  description?: string;
  collapsible?: boolean;
  repeatableGroup?: boolean;
  maxRepetitions?: number;
  layout?: 'default' | 'org-chart'; // Visual layout mode for the section
}

export type FormFieldType =
  | 'text'
  | 'textarea'
  | 'number'
  | 'phone'
  | 'email'
  | 'select'
  | 'multiselect'
  | 'boolean'
  | 'date'
  | 'datetime'
  | 'time'
  | 'table'
  | 'map'
  | 'repeating';

export interface MapFieldConfig {
  source: 'incident' | 'custom';
  editable: boolean;
  showLegend: boolean;
}

export interface FormField {
  name: string;
  type: FormFieldType;
  label: string;
  required?: boolean;
  options?: string[];
  conditional?: { field: string; value: unknown };
  sectionId: string;
  placeholder?: string;
  helpText?: string;
  autofillSource?: string;
  minLength?: number;
  maxLength?: number;
  pattern?: string;
  min?: number;
  max?: number;
  tableColumns?: FormField[];
  mapConfig?: MapFieldConfig;

  // Repeating field config (when type='repeating')
  repeatFieldType?: 'text' | 'textarea'; // Widget type for each entry, defaults to 'textarea'
  minEntries?: number;
  maxEntries?: number;

  // Contact integration
  contactEnabled?: boolean;           // If true, render as autocomplete with contact search
  contactFieldPath?: string;          // Base path for field group (e.g., "incident_commander")
  contactFieldMapping?: {             // Maps contact fields to this field's siblings
    name?: string;
    email?: string;
    phone?: string;
    organization?: string;
    title?: string;
    ics_position?: string;
  };
  contactIcsPositionFilter?: string;  // Suggested ICS position for filtering contacts
}

export interface FormFieldSchema {
  sections: FormSection[];
  fields: FormField[];
}

// ============================================================================
// Template Types
// ============================================================================

export interface FormTemplate {
  id: string;
  tenant_id: string | null;
  code: string;
  name: string;
  description: string | null;
  version: string;
  category: string | null;
  template_type?: string;
  field_schema: FormFieldSchema;
  pdf_layout: Record<string, unknown>;
  autofill_mappings: Record<string, string>;
  incident_update_mappings?: Record<string, string>; // Maps form fields to incident/op_period fields updated on approval
  signature_fields: string[];
  is_active: boolean;
  is_system: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
}

// ============================================================================
// Form Instance Types
// ============================================================================

export type FormStatus =
  | 'draft'
  | 'in_progress'
  | 'pending_review'
  | 'pending_signature'
  | 'approved'
  | 'superseded'
  | 'archived';

export interface Form {
  id: string;
  tenant_id: string;
  incident_id: string | null;
  operational_period_id: string | null;
  form_number: string;
  template_id: string;
  form_code: string;
  title: string;
  status: FormStatus;
  form_data: Record<string, unknown>;
  prepared_at: string | null;
  approved_at: string | null;
  prepared_by: string | null;
  approved_by: string | null;
  attachments: unknown[];
  last_pdf_generated_at: string | null;
  pdf_storage_key: string | null;
  metadata: Record<string, unknown>;
  tags: string[];
  /** Optimistic locking version counter — must be supplied on every HTTP save. */
  version: number;
  created_by: string;
  created_at: string;
  updated_at: string;
  deleted_at: string | null;
}

export interface FormWithDetails extends Form {
  template?: FormTemplate;
  incident?: {
    id: string;
    name: string;
    incident_number: string;
  };
  prepared_by_user?: {
    id: string;
    name: string;
  };
  approved_by_user?: {
    id: string;
    name: string;
  };
  created_by_user?: {
    id: string;
    name: string;
  };
  signatures?: FormSignature[];
}

// ============================================================================
// Signature Types
// ============================================================================

export interface FormSignature {
  id: string;
  form_id: string;
  signer_id: string;
  signature_role: string;
  signed_at: string;
  signature_data: {
    typed_name?: string;
    signature_image?: string;
  };
  ip_address?: string;
  user_agent?: string;
  created_at: string;
  signer?: {
    id: string;
    name: string;
  };
}

// ============================================================================
// History Types
// ============================================================================

export interface FormHistory {
  id: string;
  form_id: string;
  from_status: FormStatus | null;
  to_status: FormStatus;
  changed_by: string;
  changed_at: string;
  reason: string | null;
  form_data_snapshot: Record<string, unknown> | null;
  metadata: Record<string, unknown>;
  changed_by_user?: {
    id: string;
    name: string;
  };
}

// ============================================================================
// API Types
// ============================================================================

export interface CreateFormData {
  template_id: string;
  incident_id?: string;
  operational_period_id?: string;
  title: string;
  form_data?: Record<string, unknown>;
  status?: FormStatus;
  tags?: string[];
  metadata?: Record<string, unknown>;
}

export interface UpdateFormData {
  title?: string;
  form_data?: Record<string, unknown>;
  status?: FormStatus;
  incident_id?: string;
  operational_period_id?: string;
  prepared_at?: string;
  prepared_by?: string;
  attachments?: unknown[];
  tags?: string[];
  metadata?: Record<string, unknown>;
  /** Optimistic locking version. Supply the version last read from the server. */
  expected_version?: number;
}

export interface SignFormData {
  signature_role: string;
  signature_data: {
    typed_name?: string;
    signature_image?: string;
  };
}

export interface PaginatedForms {
  forms: FormWithDetails[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
}

// ============================================================================
// Status Helpers
// ============================================================================

export const FORM_STATUS_LABELS: Record<FormStatus, string> = {
  draft: 'Draft',
  in_progress: 'In Progress',
  pending_review: 'Pending Review',
  pending_signature: 'Pending Signature',
  approved: 'Approved',
  superseded: 'Superseded',
  archived: 'Archived',
};

// Color budget: statuses map onto the severity scale (calm → elevated →
// warning) instead of one arbitrary hue per status. Semantic tokens work in
// both themes.
export const FORM_STATUS_COLORS: Record<FormStatus, string> = {
  draft: 'bg-surface-tertiary text-theme-secondary',
  in_progress: 'bg-[color:var(--severity-elevated-bg)] text-[color:var(--severity-elevated-text)]',
  pending_review: 'bg-[color:var(--severity-warning-bg)] text-[color:var(--severity-warning-text)]',
  pending_signature: 'bg-[color:var(--severity-warning-bg)] text-[color:var(--severity-warning-text)]',
  approved: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300',
  superseded: 'bg-surface-tertiary text-theme-secondary',
  archived: 'bg-surface-tertiary text-theme-tertiary',
};

// Badge variants for form statuses (shared Badge component)
export const FORM_STATUS_VARIANTS: Record<FormStatus, BadgeVariant> = {
  draft: 'neutral',
  in_progress: 'info',
  pending_review: 'warning',
  pending_signature: 'warning',
  approved: 'success',
  superseded: 'neutral',
  archived: 'neutral',
};

// Form category colors
// Color budget: category is an entity type, conveyed by label — not hue.
export const FORM_CATEGORY_COLORS: Record<string, string> = {
  command: 'bg-surface-tertiary text-theme-secondary',
  planning: 'bg-surface-tertiary text-theme-secondary',
  operations: 'bg-surface-tertiary text-theme-secondary',
  logistics: 'bg-surface-tertiary text-theme-secondary',
  finance: 'bg-surface-tertiary text-theme-secondary',
};

// Badge variants for form categories (shared Badge component)
export const FORM_CATEGORY_VARIANTS: Record<string, BadgeVariant> = {
  command: 'neutral',
  planning: 'neutral',
  operations: 'neutral',
  logistics: 'neutral',
  finance: 'neutral',
};
